/**
 * Andar Bahar — every sound, synthesised (spec: the "Lounge" bed, 88 BPM,
 * D minor pentatonic; one-shots asymmetric — the win is the only long sound,
 * a loss is one quiet static note). Same skeleton as aviator/sound.ts.
 */

const LS_SOUND = 'ab_sound';
const LS_MUSIC = 'ab_music';
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let soundOn = true, musicOn = true;
let _duckedUntil = 0;

const lsGet = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private */ } };
export function initAbSound() { soundOn = lsGet(LS_SOUND) !== '0'; return soundOn; }
export function isAbSoundOn() { return soundOn; }
export function setAbSoundOn(v: boolean) { soundOn = v; lsSet(LS_SOUND, v ? '1' : '0'); if (!v) stopAbMusic(); else if (musicOn) startAbMusic(); return v; }
export function initAbMusic() { musicOn = lsGet(LS_MUSIC) !== '0'; return musicOn; }
export function isAbMusicOn() { return musicOn; }
export function setAbMusicOn(v: boolean) { musicOn = v; lsSet(LS_MUSIC, v ? '1' : '0'); if (v && soundOn) startAbMusic(); else stopAbMusic(); return v; }

function ac(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new AC(); master = ctx.createGain(); master.gain.value = 1; master.connect(ctx.destination);
  } catch { ctx = null; }
  return ctx;
}
export function unlockAbSound() { const c = ac(); if (c && c.state === 'suspended' && !document.hidden) void c.resume(); }

function bus(gain: number) {
  if (!soundOn) return null;
  const c = ac(); if (!c || !master) return null;
  const g = c.createGain(); g.gain.value = gain; g.connect(master);
  return { c, g };
}
function tone(freq: number, dur: number, o: { type?: OscillatorType; to?: number; gain?: number; delay?: number } = {}) {
  const b = bus(o.gain ?? 0.08); if (!b) return;
  const t0 = b.c.currentTime + (o.delay ?? 0);
  const osc = b.c.createOscillator(); osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(freq, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + dur);
  b.g.gain.setValueAtTime(0.0001, t0);
  b.g.gain.exponentialRampToValueAtTime(o.gain ?? 0.08, t0 + 0.006);
  b.g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(b.g); osc.start(t0); osc.stop(t0 + dur + 0.05);
}
function noise(dur: number, o: { f0?: number; f1?: number; q?: number; gain?: number; delay?: number } = {}) {
  const b = bus(o.gain ?? 0.06); if (!b) return;
  const t0 = b.c.currentTime + (o.delay ?? 0);
  const n = Math.floor(b.c.sampleRate * dur);
  const buf = b.c.createBuffer(1, n, b.c.sampleRate); const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = b.c.createBufferSource(); src.buffer = buf;
  const f = b.c.createBiquadFilter(); f.type = 'bandpass';
  f.frequency.setValueAtTime(o.f0 ?? 1200, t0);
  if (o.f1) f.frequency.exponentialRampToValueAtTime(o.f1, t0 + dur);
  f.Q.value = o.q ?? 1;
  src.connect(f); f.connect(b.g); src.start(t0);
}

/* pitch table — D minor pentatonic */
const D2 = 73.42, F2 = 87.31, G2 = 98, A2 = 110, C3 = 130.81, D3 = 146.83, F3 = 174.61, G3 = 196, A3 = 220, C4 = 261.63, D4 = 293.66, F4 = 349.23, G4 = 392, A4 = 440, C5 = 523.25, D5 = 587.33, F5 = 698.46, G5 = 783.99, A5 = 880, C6 = 1046.5, D6 = 1174.66, F6 = 1396.91, G6 = 1567.98, A6 = 1760, C7 = 2093;
void F4;

/* ─── one-shots ───────────────────────────────────────────────────────── */
export const sSlide = () => { noise(0.09, { f0: 1800, f1: 900, q: 1.2, gain: 0.05 }); noise(0.05, { f0: 420, f1: 260, q: 0.8, gain: 0.025, delay: 0.02 }); };
export const sLand = (delay = 0) => {
  noise(0.025, { f0: 3600, f1: 2200, q: 2.5, gain: 0.11, delay });
  tone(180, 0.06, { type: 'sine', to: 90, gain: 0.07, delay: delay + 0.03 });
  noise(0.05, { f0: 300, f1: 140, q: 0.7, gain: 0.035, delay: delay + 0.03 });
};
export const sJoker = () => {
  sSlide(); sLand(0.20);
  tone(D5, 0.30, { type: 'triangle', gain: 0.11, delay: 0.32 });
  tone(G5, 0.36, { type: 'triangle', gain: 0.10, delay: 0.41 });
  noise(0.45, { f0: 5500, f1: 9000, q: 0.8, gain: 0.04, delay: 0.42 });
  tone(D6, 0.5, { type: 'sine', gain: 0.035, delay: 0.52 });
  duckAbMusic(300);
};
export const sCountTick = (n: number) => { tone(n === 1 ? C6 : A5, 0.045, { type: 'square', gain: 0.04 }); noise(0.02, { f0: 4000, f1: 3000, q: 3, gain: 0.02 }); };
export const sKnock = () => { tone(110, 0.09, { type: 'sine', to: 62, gain: 0.16 }); noise(0.07, { f0: 260, f1: 110, q: 0.9, gain: 0.13 }); noise(0.02, { f0: 2000, f1: 1400, q: 2, gain: 0.04 }); duckAbMusic(200); };
/** every dealt card: slide, land, and a tick that rises a semitone every 4 cards */
export function sDealCard(n: number, side: 'andar' | 'bahar') {
  const st = Math.min(12, Math.floor((n - 1) / 4));
  const f = 440 * Math.pow(2, st / 12);
  sSlide(); sLand(0.20);
  tone(f, 0.05, { type: side === 'andar' ? 'sine' : 'triangle', gain: 0.045, delay: 0.20 });
  abBedPhase('dealing', n);
}
export const sStamp = () => { tone(80, 0.14, { type: 'sine', to: 48, gain: 0.18 }); noise(0.09, { f0: 500, f1: 180, q: 0.8, gain: 0.12 }); noise(0.03, { f0: 3000, f1: 2000, q: 2, gain: 0.05 }); duckAbMusic(250); };
export function sWin(amount: number) {
  [D5, G5, A5, D6].forEach((f, i) => tone(f, 0.30, { type: 'triangle', gain: 0.15, delay: i * 0.075 }));
  const k = Math.round(Math.min(10, Math.max(4, 4 + Math.log(Math.max(1, amount) / 50))));
  const coins = [D6, F6, G6, A6, C7];
  for (let i = 0; i < k; i++) tone(coins[i % 5], 0.12, { type: 'sine', gain: 0.09 - 0.04 * (i / Math.max(1, k - 1)), delay: 0.34 + i * 0.055 });
  noise(0.55, { f0: 6000, f1: 10000, q: 0.7, gain: 0.045, delay: 0.36 });
  tone(D6, 0.7, { type: 'sine', gain: 0.05, delay: 0.5 });
  if (amount >= 1000) { tone(A6, 0.9, { type: 'sine', gain: 0.04, delay: 0.6 }); tone(D3, 0.35, { type: 'sine', to: D2, gain: 0.14, delay: 0.30 }); }
  duckAbMusic(700);
}
export const sLoss = () => { tone(D3, 0.35, { type: 'sine', gain: 0.06 }); tone(D4, 0.25, { type: 'triangle', gain: 0.02, delay: 0.01 }); };
export const sSatOut = () => { tone(A4, 0.10, { type: 'sine', gain: 0.06 }); tone(A4, 0.14, { type: 'sine', gain: 0.04, delay: 0.13 }); };
export const sChip = () => { tone(A5, 0.06, { type: 'sine', gain: 0.13 }); tone(C6, 0.07, { type: 'sine', gain: 0.10, delay: 0.05 }); noise(0.03, { f0: 3000, f1: 2200, q: 2, gain: 0.09 }); };
export const sUndo = () => { tone(C6, 0.06, { type: 'sine', gain: 0.10 }); tone(A5, 0.09, { type: 'sine', to: F5, gain: 0.10, delay: 0.06 }); };
export const sDenied = () => { tone(175, 0.1, { type: 'square', gain: 0.09 }); tone(175, 0.1, { type: 'square', gain: 0.09, delay: 0.14 }); };
export const sTap = () => { tone(A5, 0.045, { type: 'sine', to: F5, gain: 0.05 }); };
let lastCrowd = 0, dealing = false;
export const sCrowd = () => { const now = performance.now(); if (dealing || now < _duckedUntil || now - lastCrowd < 250) return; lastCrowd = now; noise(0.025, { f0: 4500, f1: 3500, q: 3, gain: 0.03 }); };

/* ─── the bed: "Lounge" — 88 BPM, D minor pentatonic, circling, D4 pedal ── */
const BPM = 88, BEAT = 60 / BPM, BAR = 4 * BEAT, STEP = BAR / 16, LOOP = 4 * BAR;
const M_MASTER = 0.38, DUCK_FLOOR = 0.30;
let I = 0.40, musicLive = false, loopStart = 0, nextEv = 0, bedTimer = 0;
let bedGain: GainNode | null = null;
const live: { o: AudioScheduledSourceNode; end: number }[] = [];
const CHART = [
  { bass: [D2, A2], ep: [D3, F3, A3, D4] },
  { bass: [F2, C3], ep: [F3, A3, C4, D4] },
  { bass: [G2, D3], ep: [G3, A3, C4, D4] },
  { bass: [A2, G2], ep: [G3, A3, C4, D4] },
];
const LEAD: Record<number, Record<number, number>> = { 0: { 8: A4, 10: C5, 12: D5 }, 1: {}, 2: { 8: D5, 10: C5, 12: A4 }, 3: { 10: G4, 12: A4 } };

function ep(c: AudioContext, out: GainNode, f: number, at: number, peak: number, decay: number) {
  const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 500 + 2500 * I; lp.Q.value = 0.7;
  const g = c.createGain(); g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, at + decay);
  for (const cents of [-5, 5]) {
    const o = c.createOscillator(); o.type = 'triangle'; o.frequency.value = f; o.detune.value = cents;
    o.connect(lp); o.start(at); o.stop(at + decay + 0.05); live.push({ o, end: at + decay + 0.05 });
  }
  lp.connect(g); g.connect(out);
}
function bedTone(f: number, dur: number, o: { type?: OscillatorType; to?: number; gain?: number; delay?: number }) {
  /* one-shots on the BED bus so the duck covers them */
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t0 = c.currentTime + (o.delay ?? 0);
  const osc = c.createOscillator(); osc.type = o.type ?? 'sine'; osc.frequency.setValueAtTime(f, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + dur);
  const g = c.createGain(); g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(o.gain ?? 0.05, t0 + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g); g.connect(bedGain); osc.start(t0); osc.stop(t0 + dur + 0.05);
  live.push({ o: osc, end: t0 + dur + 0.05 });
}
function bedNoise(dur: number, o: { f0: number; f1: number; q: number; gain: number; delay?: number }) {
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t0 = c.currentTime + (o.delay ?? 0);
  const n = Math.floor(c.sampleRate * dur); const buf = c.createBuffer(1, n, c.sampleRate); const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = c.createBufferSource(); src.buffer = buf;
  const f = c.createBiquadFilter(); f.type = 'bandpass'; f.frequency.setValueAtTime(o.f0, t0); f.frequency.exponentialRampToValueAtTime(o.f1, t0 + dur); f.Q.value = o.q;
  const g = c.createGain(); g.gain.value = o.gain;
  src.connect(f); f.connect(g); g.connect(bedGain); src.start(t0);
  live.push({ o: src, end: t0 + dur });
}
function pump() {
  const c = ctx; if (!c || !musicLive || !bedGain) return;
  const horizon = c.currentTime + 0.20;
  const earliest = c.currentTime - STEP;
  if (nextEv < earliest) nextEv = loopStart + Math.ceil((earliest - loopStart) / STEP) * STEP;
  while (nextEv < horizon) {
    const pos = (nextEv - loopStart) % LOOP;
    const bar = Math.floor(pos / BAR) % 4, s = Math.round((pos % BAR) / STEP) % 16;
    const d = Math.max(0, nextEv - c.currentTime), at = nextEv;
    const ch = CHART[bar], hg = 0.012 + 0.03 * I;
    if (I >= 0.25 && s % 4 === 2) bedNoise(0.02, { f0: 7000, f1: 5500, q: 2.5, gain: hg, delay: d + 0.06 * BEAT });
    if (I >= 0.25 && (s === 4 || s === 12)) bedNoise(0.07, { f0: 5000, f1: 3000, q: 1.0, gain: hg * 1.4, delay: d });
    const bg = 0.07 * (0.5 + 0.5 * I);
    if (s === 0) { bedTone(ch.bass[0], 0.55, { gain: bg, delay: d }); bedTone(ch.bass[0] * 2, 0.35, { type: 'triangle', gain: 0.02, delay: d }); }
    if (s === 6) bedTone(ch.bass[0], 0.25, { gain: bg * 0.8, delay: d });
    if (s === 12) { bedTone(ch.bass[1], 0.40, { gain: bg, delay: d }); bedTone(ch.bass[1] * 2, 0.30, { type: 'triangle', gain: 0.02, delay: d }); }
    if (s === 0) for (const f of ch.ep) ep(c, bedGain, f, at, 0.035 + 0.03 * I, 1.4);
    if (I >= 0.3 && s === 6) ep(c, bedGain, ch.ep[2], at, 0.022 + 0.02 * I, 0.9);
    if (I >= 0.3 && s === 12) ep(c, bedGain, ch.ep[3], at, 0.022 + 0.02 * I, 0.9);
    if (I >= 0.60) { const f = LEAD[bar][s]; if (f) bedTone(f, 0.55, { type: 'triangle', gain: 0.045 * (0.6 + 0.4 * I), delay: d }); }
    if (I >= 0.65 && (s === 0 || s === 8)) bedTone(55, 0.12, { type: 'sine', to: 40, gain: 0.08, delay: d });
    nextEv += STEP;
  }
  if (live.length > 128) { const now = c.currentTime; for (let i = live.length - 1; i >= 0; i--) if (live[i].end < now) live.splice(i, 1); }
}
export function abBedPhase(p: 'betting' | 'dealing' | 'result' | 'intermission', n = 0) {
  if (p === 'betting') { I = 0.40; dealing = false; }
  if (p === 'dealing') { I = 0.50 + 0.30 * Math.min(1, n / 20); dealing = true; }
  if (p === 'result') { I = 0.30; dealing = false; }
  if (p === 'intermission') { I = 0.40; dealing = false; }
}
export function startAbMusic() {
  if (musicLive || !soundOn || !musicOn) return;
  const c = ac(); if (!c || !master) return;
  bedGain = c.createGain(); bedGain.gain.value = 0.0001; bedGain.connect(master);
  bedGain.gain.exponentialRampToValueAtTime(M_MASTER, c.currentTime + BAR);
  musicLive = true; loopStart = c.currentTime + 0.10; nextEv = loopStart;
  pump(); bedTimer = window.setInterval(pump, 50);
}
export function stopAbMusic() {
  musicLive = false; window.clearInterval(bedTimer); _duckedUntil = 0;
  const c = ctx, g = bedGain, mine = live.splice(0); bedGain = null;
  if (!c || !g) return;
  const now = c.currentTime;
  g.gain.cancelScheduledValues(now); g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
  for (const nd of mine) { try { nd.o.stop(now + 0.18); } catch { /* done */ } }
  window.setTimeout(() => { try { g.disconnect(); } catch { /* gone */ } }, 420);
}
export function duckAbMusic(ms: number) {
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t = c.currentTime;
  bedGain.gain.cancelScheduledValues(t); bedGain.gain.setValueAtTime(Math.max(bedGain.gain.value, 0.0001), t);
  bedGain.gain.exponentialRampToValueAtTime(M_MASTER * DUCK_FLOOR, t + 0.05);
  bedGain.gain.setValueAtTime(M_MASTER * DUCK_FLOOR, t + ms / 1000);
  bedGain.gain.exponentialRampToValueAtTime(M_MASTER, t + ms / 1000 + 0.5);
  _duckedUntil = performance.now() + ms + 500;
}
export function bindAbVisibility() {
  const h = () => { const c = ctx; if (!c) return; if (document.hidden) { void c.suspend(); } else if (soundOn) { void c.resume(); } };
  document.addEventListener('visibilitychange', h);
  return () => document.removeEventListener('visibilitychange', h);
}
